import { ILXAnalytics } from '@zhenguo/types'
import Cookies from 'js-cookie'
import getURLSearchParam, { getURLSearchParams } from './base/url'
import { isEnvProd, isNodeProd } from './base/env'
import debug from './base/debug'
import { JSONparse } from './base/storage'
import Agent from './agent'
import { reportWarn } from './owl'

export interface LxLab {
  goods_id?: number
  custom?: Record<string, string | number | boolean | undefined>
}

const isProd = isEnvProd() && isNodeProd()

function lxLogLocal(...args: any[]) {
  if (!debug['lx']) return
  if (!isProd || !window.Analytics) {
    console.log(
      `%c[lx.${args[0]}]`,
      `background:${args[1]}; padding: 3px; border-radius: 3px;  color: #000`,
      ...args.slice(2),
    )
    console.table(args[3]?.custom)
  }
}

let preuserId: number | string

function _lxSetUID() {
  const userId = Cookies.get('userId') || Cookies.get('u') || Cookies.get('mt-userid')
  if (userId && preuserId !== userId) {
    preuserId = userId
    lxLogLocal('uid', 'pink', userId)
    window.Analytics && window.Analytics('set', 'uid', userId)
  }
}

export function genValLab(_valLab?: LxLab) {
  const valLab = _valLab || {}
  const _custom = {
    user_type: 'guest',
    page_category: 'h5',
    url: window.location.href,
    phx_wake_up_type: getURLSearchParam('phx_wake_up_type'),
    phx_wake_up_source: getURLSearchParam('phx_wake_up_source'),
    ...window.LX_PAGE_LAB_CUSTOM,
    ...(valLab.custom || {}),
  }
  return { ...(valLab || {}), custom: _custom }
}

export function lxPV(cid: string = window.LX_PAGE_CID, valLab?: LxLab, environment?: {}) {
  const lib = genValLab(valLab)
  lxLogLocal('pv', '#f84c49', cid, lib, environment)
  _lxSetUID()
  window.Analytics && window.Analytics('pageView', lib, environment || {}, cid)
}

export function lxTag(
  tagIdentifier: string,
  tagKey: string | Record<string, string>,
  tagValue?: string | Record<string, string | number>,
) {
  if (tagValue) {
    lxLogLocal('tag', '#ffcb00', tagIdentifier, tagKey, tagValue)
    window.Analytics && window.Analytics('tag', tagIdentifier, tagKey as string, tagValue)
    return
  }
  if (typeof tagKey === 'string') return
  lxLogLocal('tag', '#ffcb00', tagIdentifier, tagKey)
  window.Analytics && window.Analytics('tag', tagIdentifier, tagKey)
}

export function lxMC(bid?: string, valLab?: LxLab, options = {}) {
  if (!bid || bid === 'undefined') return
  const lib = genValLab(valLab)
  lxLogLocal('mc', '#3e88ff', bid, lib, options)
  _lxSetUID()
  window.Analytics &&
    window.Analytics('moduleClick', bid, lib, {
      ...options,
      withPageInfo: true,
    })
}

export function lxTagMC(bid?: string, valLab?: LxLab) {
  if (!bid || bid === 'undefined') return
  const lib = genValLab(valLab)
  lxTag('airbnb', window.LX_PAGE_CID, { bid, ...lib.custom })
  lxMC(bid, valLab, {
    isLeave: true,
  })
}

export function lxMV(bid: string, valLab?: LxLab) {
  if (!bid || bid === 'undefined') return
  const lib = genValLab(valLab)
  lxLogLocal('mv', '#41b883', bid, lib)
  _lxSetUID()
  window.Analytics && window.Analytics('moduleView', bid, lib)
}

export function getUTM(utm: Record<string, string | null>) {
  const searchParams = getURLSearchParams()
  utm = {
    utm_source: searchParams.get('utm_source') || Agent.appnm,
    utm_medium: searchParams.get('utm_medium') || searchParams.get('phx_wake_up_type'),
    utm_term: searchParams.get('utm_term'),
    utm_content:
      searchParams.get('utm_content') ||
      searchParams.get('phx_wake_up_source') ||
      searchParams.get('phx_ad_delivery_id'),
    utm_campaign: searchParams.get('utm_campaign') || searchParams.get('phx_wake_up_type'),
    ...utm,
  }
  const newUTM: Record<string, string | null> = {}
  for (const i of Object.keys(utm)) {
    utm[i] && (newUTM[i] = utm[i] || '')
  }
  return newUTM
}

export function lxUTM(utm: Record<string, string | null>) {
  window.Analytics && window.Analytics('set', 'utm', getUTM(utm))
}

export function autoMC(event: Event) {
  if (!event || !event.target) return
  const bid = (event.target as HTMLElement).dataset['bid']
  if (bid) {
    lxMC(bid)
  }
}

export const autoMV = new IntersectionObserver((entries) => {
  for (const entry of entries) {
    if (entry.isIntersecting) {
      const target = entry.target as HTMLElement
      if (target.dataset['mv'] && target.dataset['mv'] !== 'undefined') {
        const bid = target.dataset['mv']
        const labstr = target.dataset['lab'] || ''
        let lab = {}
        if (labstr) {
          lab = JSONparse(labstr, {})
        }
        lxMV(bid, lab)
        autoMV.unobserve(target)
      } else if (target.dataset['viewBid'] && target.dataset['viewBid'] !== 'undefined') {
        const bid = target.dataset['viewBid']
        const labstr = target.dataset['lab'] || ''
        let lab = {}
        if (labstr) {
          lab = JSONparse(labstr, {})
        }
        lxMV(bid, lab)
        autoMV.unobserve(target)
      }
    }
  }
})

let nodes: Element[] = []

export const subscribeMV = function (node: HTMLElement | null) {
  if (!node) return
  const observer = new MutationObserver(() => {
    const newnodes: Element[] = []
    document.querySelectorAll('[data-view-bid]').forEach((i) => {
      if (!nodes.includes(i)) {
        autoMV.observe(i)
        newnodes.push(i)
      }
    })
    nodes = nodes.concat(newnodes)
  })

  observer.observe(node, {
    childList: true,
    attributes: false,
    characterData: false,
    subtree: true,
  })
}

export function getLxTags() {
  return new Promise((resolve) => {
    if (!window.Analytics) {
      resolve({})
      return
    }
    window.Analytics('tag', (err: any, tag: Record<string, any>) => {
      if (err) {
        reportWarn({ name: '获取 tag 错误', msg: err })
        resolve({})
        return
      }
      resolve(tag)
    })
  })
}

declare global {
  interface Window {
    LX_PAGE_CID: string
    LX_PAGE_LAB_CUSTOM: {}
    Analytics: ILXAnalytics | undefined
  }
}
